<template>
  <div>
    <back-button default-to="/stocks"/>
    <b-card no-header>
      <b-row>
        <b-col cols="12" md="6">
          <div class="mb-3">
            <label class="form-label w-100" style="font-size: 80%;">Status</label>
            <b-badge :variant="statusVariant(approvalStatus)">{{ statusLabel(approvalStatus) }}</b-badge>
          </div>
          <b-alert :show="approvalStatus === 'returned' && !!reviewNote" variant="warning">
            Dikembalikan untuk diperbaiki: {{ reviewNote }}
          </b-alert>
          <b-alert :show="approvalStatus === 'rejected' && !!reviewNote" variant="danger">
            Ditolak: {{ reviewNote }}
          </b-alert>
          <p v-if="reviewInfo.submitted_at" class="text-muted small">Diajukan oleh {{ actorName(reviewInfo.submitter) }} pada {{ formatDateTime(reviewInfo.submitted_at) }}.</p>
          <p v-if="reviewInfo.edited_at" class="text-muted small">Terakhir diubah oleh {{ actorName(reviewInfo.editor) }} pada {{ formatDateTime(reviewInfo.edited_at) }}.</p>
          <div class="d-flex">
          <div class="mb-3"><label class="form-label w-100" style="font-size: 80%;">Barcode</label>
              <p class="heading-color">{{barcode}} <a v-if="barcode" href="#" @click.prevent="copyBarcode" style="border-radius: 4px;" class="bg-secondary px-2 py-1 text-light"><font-awesome-icon icon="clipboard"/></a></p>
            </div>
            <input type="text" id="barcode-text" v-model="barcode" style="position: absolute; z-index:-1111;">
          </div>          
          <div class="mb-3">
            <label class="form-label w-100" style="font-size: 80%;">Saldo Stok</label>
            <p class="heading-color">{{ formatBalance(currentValues) }}</p>
          </div>
          <template v-for="(m, index) in model">
            <div :key="index" v-if="index !== 'opening_quantity'">
              <detail-group-input :ref="index" :type="m.type" :label="index === 'berat' ? 'Berat' : (typeof m.label === 'string' ? m.label : null)" :need-label="true" v-model="m.value" :id="index" :skipPriceTag="index === 'berat'" />
            </div>            
          </template>
        </b-col>
      </b-row>
      <div v-if="isAdmin && approvalStatus === 'on_review'" class="mb-3">
        <b-button variant="success" :disabled="isLoading" @click="approve">Setujui</b-button>
        <b-button variant="warning" class="ml-2" :disabled="isLoading" @click="showDecline('return')">Kembalikan</b-button>
        <b-button variant="danger" class="ml-2" :disabled="isLoading" @click="showDecline('reject')">Tolak</b-button>
      </div>
      <div v-if="isApproved" class="mb-3">
        <approval-request-actions
          target-type="stock"
          :target-id="$route.params.id"
          :created-by="createdBy"
          :change-fields="changeFields"
          :current-values="currentValues"
          @submitted="getDetail" />
      </div>
      <div v-if="isApproved" class="mb-3">
        <b-button variant="warning" :to="'/stock-adjustments/create?stock_id=' + $route.params.id">Ajukan Penyesuaian Stok</b-button>
      </div>
      <div v-if="isApproved" class="mb-3">
        <b-button v-if="!printRequest" variant="info" :disabled="isLoading" @click="requestBarcodePrint">Ajukan Cetak Barcode</b-button>
        <b-badge v-else-if="printRequest.status === 'on_review'" variant="warning">Pengajuan cetak barcode sedang direview</b-badge>
        <span v-else-if="printRequest.status === 'returned'"><b-badge variant="warning">Pengajuan dikembalikan</b-badge><small v-if="printRequest.review_note" class="text-muted ml-2">{{ printRequest.review_note }}</small><b-button variant="warning" class="ml-2" :disabled="isLoading" @click="requestBarcodePrint">Ajukan Ulang Cetak Barcode</b-button></span>
        <b-button v-else-if="printRequest.status === 'approved'" variant="success" :to="'/print/barcodes?stock_id=' + $route.params.id + '&approval_request_id=' + printRequest.id">Cetak Barcode</b-button>
      </div>
      <div>
        <b-button v-if="canEdit" :to="'/stocks/' + $route.params.id + '/edit'" variant="primary" class="mt-0">Ubah</b-button>
        <b-button v-if="canSubmit" variant="primary" class="mt-0 ml-2" :disabled="isLoading" @click="submit">Ajukan Persetujuan</b-button>
        <b-button v-if="canEdit" variant="danger" class="mt-0 ml-2" :disabled="isLoading" @click="removeDraft">Hapus Draft</b-button>
        <b-button :to="'/stocks/'" variant="secondary" class="ml-3 float-right">Kembali</b-button>
      </div>
    </b-card>
    <b-modal ref="declineModal" :title="declineMode === 'return' ? 'Kembalikan untuk Diperbaiki' : 'Tolak Stok'" :ok-title="declineMode === 'return' ? 'Kembalikan' : 'Tolak'" cancel-title="Batal" @ok="decline">
      <form-group-input type="textarea" label="Catatan Alasan" :need-label="true" v-model="declineNote" id="review_note" />
    </b-modal>
  </div>
</template>

<script>
import { setModelValue, Roles } from '@/_helpers'
import detailGroupInput from '@/components/DetailGroupInput.vue'
import formGroupInput from '@/components/FormGroupInput.vue'
import approvalRequestActions from '@/components/ApprovalRequestActions.vue'
import Stock from '@/models/Stock.js'
import ApprovalRequest from '@/models/ApprovalRequest.js'

const stockModel = new Stock()
const approvalRequestModel = new ApprovalRequest()

export default {
  components: {
    detailGroupInput,
    formGroupInput,
    approvalRequestActions
  },
  computed: {
    isAdmin () {
      return this.$store.getters['account/accountData'].role === Roles.ADMIN
    },
    isApproved () {
      return this.approvalStatus === 'approved'
    },
    changeFields () {
      return [
        { key: 'nama', label: 'Nama' },
        { key: 'berat', label: 'Berat (Gram)', inputmode: 'decimal' },
        { key: 'kadar', label: 'Kadar' },
        { key: 'harga_modal', label: 'Harga Modal', type: 'number' },
        { key: 'harga_jual', label: 'Harga Jual', type: 'number' }
      ]
    },
    isCreator () {
      return Number(this.createdBy) === Number(this.$store.getters['account/accountData'].id)
    },
    canEdit () {
      return this.isCreator && ['draft', 'returned'].indexOf(this.approvalStatus) !== -1
    },
    canSubmit () {
      return this.canEdit
    }
  },
  watch: {
    '$route.params.id': function (val) {
      if (val !== undefined) {
        this.getDetail()
      }
    }
  },
  data () {
    return {
      isLoading: false,
      barcode: null,
      approvalStatus: null,
      reviewNote: null,
      createdBy: null,
      reviewInfo: {},
      declineMode: 'return',
      declineNote: '',
      currentValues: {},
      printRequest: null,
      model: stockModel.init()
    }
  },
  mounted () {
    this.getDetail()
  },
  methods: {
    statusLabel (status) {
      return { draft: 'Draft', on_review: 'On Review', approved: 'Disetujui', returned: 'Dikembalikan', rejected: 'Ditolak' }[status] || status
    },
    statusVariant (status) {
      return { draft: 'secondary', on_review: 'warning', approved: 'success', returned: 'warning', rejected: 'danger' }[status] || 'secondary'
    },
    async runAction (action, successMessage) {
      this.isLoading = true
      try {
        await action()
        this.$store.dispatch('notification/success', successMessage)
        await this.getDetail()
      } catch (e) {
        this.$store.dispatch('notification/error', e)
      }
      this.isLoading = false
    },
    submit () {
      return this.runAction(() => stockModel.submit(this.$route.params.id), 'Stok berhasil diajukan.')
    },
    approve () {
      return this.runAction(() => stockModel.approve(this.$route.params.id), 'Stok berhasil disetujui.')
    },
    showDecline (mode) {
      this.declineMode = mode
      this.declineNote = ''
      this.$refs.declineModal.show()
    },
    decline () {
      const note = this.declineNote
      const mode = this.declineMode
      return this.runAction(
        () => mode === 'return' ? stockModel.returnForRevision(this.$route.params.id, note) : stockModel.reject(this.$route.params.id, note),
        mode === 'return' ? 'Stok dikembalikan untuk diperbaiki.' : 'Stok ditolak.'
      )
    },
    copyBarcode () {
      let copyText = document.getElementById("barcode-text");
      // Select the text field
      copyText.select();
      copyText.setSelectionRange(0, 99999); // For mobile devices

      // Copy the text inside the text field
      navigator.clipboard.writeText(copyText.value);
      this.$store.dispatch('notification/success', 'Barcode berhasil di copy.')
    },
    actorName (actor) { return actor && actor.name ? actor.name : '-' },
    formatDateTime (value) { return value ? this.$moment(value).format('DD/MM/YYYY HH:mm') : '-' },
    formatWeight (value) {
      return Number(value || 0).toLocaleString('id-ID', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' gram'
    },
    formatBalance (stock) {
      if (stock.stock_balance === null || stock.stock_balance === undefined) return '-'
      const amount = Number(stock.stock_balance || 0)
      const isGram = stock.inventory_unit === 'gram'
      const formatted = isGram
        ? amount.toLocaleString('id-ID', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : amount.toLocaleString('id-ID', { maximumFractionDigits: 0 })
      const unitLabel = isGram ? 'gram' : 'buah'
      return formatted + ' ' + unitLabel
    },
    async removeDraft () {
      if (!window.confirm('Hapus draft stok ini?')) return
      this.isLoading = true
      try {
        await stockModel.delete(this.$route.params.id)
        this.$store.dispatch('notification/success', 'Draft stok dihapus.')
        this.$router.push('/stocks')
      } catch (e) {
        this.$store.dispatch('notification/error', e)
      }
      this.isLoading = false
    },
    filterModel (search) {
      const filtered = Object.entries(this.model).filter(mdl => {
        return search.includes(mdl[0])
      });
      let result = {}
      filtered.forEach(element => {
        result[element[0]] = element[1];
      });
      return result;
    },
    async getDetail () {
      this.isLoading = true
      try {
        const resp = await stockModel.find(this.$route.params.id);
        this.barcode = resp.stock_barcode
        this.approvalStatus = resp.approval_status
        this.reviewNote = resp.review_note
        this.createdBy = resp.created_by
        this.reviewInfo = resp
        this.currentValues = resp
        if (resp.approval_status === 'approved') {
          const printRequest = await approvalRequestModel.findPrintRequest('stock', this.$route.params.id)
          // The API can return an empty object when no print request exists.
          // Treat it as null so the request button remains visible.
          this.printRequest = printRequest && printRequest.id ? printRequest : null
        } else {
          this.printRequest = null
        }
        setModelValue(this.model, resp);
        if (this.model.berat && resp.berat !== null && resp.berat !== undefined) {
          this.model.berat.value = this.formatWeight(resp.berat)
        }
      }
      catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(e)
        }
      }

      this.isLoading = false
    },
    async requestBarcodePrint () {
      this.isLoading = true
      try {
        this.printRequest = this.printRequest && this.printRequest.status === 'returned'
          ? await approvalRequestModel.resubmitPrint(this.printRequest.id)
          : await approvalRequestModel.requestPrintBarcode('stock', this.$route.params.id)
        this.$store.dispatch('notification/success', 'Pengajuan cetak barcode dikirim ke review.')
      } catch (e) {
        this.$store.dispatch('notification/error', e)
      }
      this.isLoading = false
    }
  }
}
</script>
